// "Varnost" (administrators): two-factor authentication for everyone, and a notice when someone
// seems to be guessing passwords (many failed sign-ins from many addresses).
import { useState } from 'react';
import type { LoginAttackAlert, User } from '../../../../shared/types';
import { getSecurityAlerts, getSecuritySettings, saveSecuritySettings } from '../../lib/account';
import { formatDateTime, plural } from '../../lib/format';
import { accountErrorText } from '../../components/AccountForms';
import { confirmDialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { ReauthCancelled, useReauth } from '../../components/ReauthDialog';
import { toast } from '../../components/Toasts';
import { LoadError, Loading, useLoaded } from './common';

/** Alarms of the last week are shown; older ones are in the audit log. */
const ATTACK_NOTICE_MS = 7 * 24 * 3600 * 1000;

/**
 * Mandatory 2FA and the Android app of a user without it: the app stays signed in, but the server
 * refuses its uploads (403 restricted); they are marked failed and not retried by themselves.
 */
const ANDROID_WHILE_2FA_MISSING =
  'Njihova aplikacija Android ostane prijavljena, vendar posnetkov ne bo pošiljala na strežnik, dokler v spletni aplikaciji ne vklopijo dvostopenjske prijave. Neposlani posnetki ostanejo v telefonu; nato jih v aplikaciji pošljejo z gumbom »Poskusi znova«.';

function AttackNotice({ attacks }: { attacks: LoginAttackAlert[] }) {
  const recent = attacks.filter((a) => Date.now() - Date.parse(a.at) < ATTACK_NOTICE_MS);
  const latest = recent[0];
  if (!latest) return null;
  return (
    <div className="notice notice--warning" role="status" id="attack-notice">
      <Icon name="alert" size={20} className="notice__icon" />
      <div className="notice__content">
        <p>
          <strong>Nekdo je verjetno poskušal uganiti gesla.</strong> {formatDateTime(latest.at)}: v 15 minutah{' '}
          {plural(latest.failures, ['neuspela prijava', 'neuspeli prijavi', 'neuspele prijave', 'neuspelih prijav'])}, različnih naslovov:{' '}
          {latest.addresses}.{recent.length > 1 ? ` V zadnjem tednu se je to zgodilo ${recent.length}-krat.` : ''}
        </p>
        <p className="muted small">
          Prijave s pravilnim geslom in kodo so delovale normalno. Poskrbite, da imajo vsi uporabniki dolga gesla in vklopljeno dvostopenjsko
          prijavo; podrobnosti so v varnostnem dnevniku.
        </p>
      </div>
    </div>
  );
}

export function SecuritySection({ me }: { me: User }) {
  const settings = useLoaded(getSecuritySettings, 'Varnostnih nastavitev ni bilo mogoče naložiti.');
  const alerts = useLoaded(getSecurityAlerts, 'Varnostnih opozoril ni bilo mogoče naložiti.');
  const [busy, setBusy] = useState(false);
  const reauth = useReauth(me.totpEnabled);
  const require2fa = settings.data?.require2fa ?? true;

  const toggle = async () => {
    const next = !require2fa;
    const ok = await confirmDialog(
      next
        ? {
            title: 'Vklopim obvezno dvostopenjsko prijavo?',
            message: `${
              me.totpEnabled
                ? 'Uporabniki, ki je še nimajo, jo bodo morali vklopiti, preden bodo lahko nadaljevali delo.'
                : 'Uporabniki, ki je še nimajo, jo bodo morali vklopiti, preden bodo lahko nadaljevali delo. Tudi vi je še nimate – vklopiti jo boste morali takoj.'
            } ${ANDROID_WHILE_2FA_MISSING}`,
            confirmLabel: 'Vklopi',
          }
        : {
            title: 'Izklopim obvezno dvostopenjsko prijavo?',
            message:
              'Uporabniki bodo lahko dvostopenjsko prijavo izklopili, novi pa se bodo prijavljali samo z geslom. Kdor izve takšno geslo, ima dostop do vseh sestankov in nadzorov podjetja. Priporočamo, da ostane vklopljena.',
            confirmLabel: 'Izklopi',
            danger: true,
          },
    );
    if (!ok) return;
    setBusy(true);
    try {
      settings.set(await reauth.guard(() => saveSecuritySettings({ require2fa: next })));
      toast(next ? 'Dvostopenjska prijava je zdaj obvezna.' : 'Dvostopenjska prijava ni več obvezna.', { kind: next ? 'success' : 'warning' });
    } catch (err) {
      if (!(err instanceof ReauthCancelled)) toast(accountErrorText(err, 'Nastavitve ni bilo mogoče shraniti.'), { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="security-title" id="varnost">
      <h2 id="security-title" className="card__title">
        <Icon name="shield" size={22} /> Varnost
      </h2>
      {alerts.data && <AttackNotice attacks={alerts.data.attacks} />}
      {settings.error && !settings.data ? (
        <LoadError message={settings.error} onRetry={settings.reload} />
      ) : !settings.data ? (
        <Loading />
      ) : (
        <>
          <label className="switch-row" htmlFor="require-2fa">
            <span className="switch-row__text">
              <span className="switch-row__title">Obvezna dvostopenjska prijava za vse uporabnike</span>
              <span className="muted small">Ob prijavi je poleg gesla potrebna še koda iz aplikacije na telefonu.</span>
            </span>
            <input
              id="require-2fa"
              type="checkbox"
              role="switch"
              className="switch"
              checked={require2fa}
              disabled={busy}
              onChange={() => void toggle()}
            />
          </label>
          {!require2fa && (
            <div className="notice notice--warning" role="status">
              <Icon name="alert" size={20} className="notice__icon" />
              <div className="notice__content">
                <p>
                  Dvostopenjska prijava ni obvezna. Računi brez nje so zaščiteni le z geslom – posnetki in zapisniki so dosegljivi iz interneta.
                </p>
              </div>
            </div>
          )}
        </>
      )}
      {reauth.dialog}
    </section>
  );
}
